import React, { useState, useEffect, useMemo, useCallback } from 'react';
import supabase from '../data/supabase';
import { Select } from '../components/UI';
import { useToast } from '../components/Toast';

/* ═══════════════════════════════════════════════════
   PUBLIC BOOKING PAGE
   Clean, Apple-level scheduling interface
   No auth required — loads via RPC function
   ═══════════════════════════════════════════════════ */

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const TIMEZONE_OPTIONS = [
  "America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Anchorage","Pacific/Honolulu",
  "America/Phoenix","America/Toronto","America/Vancouver","America/Mexico_City","America/Bogota","America/Sao_Paulo",
  "America/Argentina/Buenos_Aires","Europe/London","Europe/Dublin","Europe/Paris","Europe/Berlin","Europe/Madrid",
  "Europe/Rome","Europe/Amsterdam","Europe/Stockholm","Europe/Helsinki","Europe/Moscow","Africa/Cairo",
  "Africa/Johannesburg","Africa/Lagos","Asia/Dubai","Asia/Kolkata","Asia/Bangkok","Asia/Singapore",
  "Asia/Shanghai","Asia/Tokyo","Asia/Seoul","Australia/Sydney","Australia/Melbourne","Australia/Perth",
  "Pacific/Auckland","Pacific/Fiji",
];

export default function BookingPage({ slug, meetingSlug }) {
  const toast = useToast();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Flow state
  const [step, setStep] = useState("types"); // types → calendar → time → form → confirmed
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Form
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Timezone — auto-detect guest, host comes from DB
  const [guestTz, setGuestTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago");
  const hostTz = pageData?.host?.timezone || pageData?.page?.timezone || "America/Chicago";
  const [teamMembers, setTeamMembers] = useState([]);

  // Load booking page data
  useEffect(() => {
    const load = async () => {
      try {
        // PERSONAL FLOW: URL is /book/<owner-slug>/<type-slug>. Use the
        // v2 RPC which scopes booking_types by BOTH owner and type slug.
        // This eliminates the cross-user collision where /book/X/Y
        // returned a different user's row because the v1 RPC returned
        // all of the owner's types and the page filter ran client-side.
        if (meetingSlug) {
          const { data, error: err } = await supabase.rpc("get_booking_page_v2", {
            p_owner_slug: slug, p_type_slug: meetingSlug,
          });
          if (!err && data?.page && data?.booking_types?.length > 0) {
            setPageData(data);
            setSelectedType(data.booking_types[0]);
            setStep("calendar");
          } else {
            setError("Page not found");
          }
        } else {
          // ROUND-ROBIN FLOW: URL is /book/<type-slug> (single segment).
          // booking_types.slug is unique per (org_id, slug) but not
          // globally — fetch the single match and resolve its owning
          // scheduling_page via the v1 RPC (preserved). For round-robin
          // types this is fine: scheduling_type === 'round_robin' types
          // have unique slugs in practice.
          const { data: btData } = await supabase.from("booking_types")
            .select("*")
            .eq("slug", slug)
            .eq("scheduling_type", "round_robin")
            .limit(1)
            .maybeSingle();
          if (btData) {
            const { data: spData } = await supabase.from("scheduling_pages")
              .select("*")
              .eq("user_id", btData.user_id)
              .limit(1)
              .maybeSingle();
            if (spData) {
              const { data: fullPage } = await supabase.rpc("get_booking_page", { page_slug: spData.slug });
              if (fullPage?.page) {
                setPageData(fullPage);
                const match = fullPage.booking_types?.find(bt => bt.slug === slug);
                if (match) { setSelectedType(match); setStep("calendar"); }
                else { setError("Page not found"); }
              } else { setError("Page not found"); }
            } else { setError("Page not found"); }
          } else { setError("Page not found"); }
        }
      } catch (e) { setError("Something went wrong"); }
      setLoading(false);
    };
    load();
  }, [slug, meetingSlug]);

  const page = pageData?.page;
  const host = pageData?.host;
  const bookingTypes = pageData?.booking_types || [];

  // Load team members for round robin display — start early, preload images.
  // Fetches via server endpoint (service-role) so no anon SELECT on public.users
  // is required. See migration 045_rls_hole_closure.
  useEffect(() => {
    if (!slug) return;
    if (!selectedType || selectedType.scheduling_type !== "round_robin") return;
    (async () => {
      try {
        const resp = await fetch(`/api/calendar/booking-team?slug=${encodeURIComponent(slug)}`);
        if (!resp.ok) return;
        const { team } = await resp.json();
        if (Array.isArray(team)) {
          setTeamMembers(team);
          // Preload images so they appear instantly
          team.forEach(u => {
            if (u.avatar_url) {
              const img = new Image();
              img.src = u.avatar_url;
            }
          });
        }
      } catch (e) {}
    })();
  }, [slug, selectedType]);
  const availability = pageData?.availability || [];
  const brandColor = page?.brand_color || "#FAFAFA";

  // Generate available dates for the calendar
  const availableDays = useMemo(() => {
    return availability.map(a => a.day_of_week);
  }, [availability]);

  // Helper: create a Date for a specific date+time in a specific timezone
  const dateInTz = useCallback((year, month, day, hours, minutes, tz) => {
    // Create an ISO string as if it's in the target timezone
    const pad = n => String(n).padStart(2, "0");
    const fake = `${year}-${pad(month + 1)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
    // Use Intl to find the offset for this timezone at this date/time
    const d = new Date(fake + "Z"); // treat as UTC first
    const utcStr = d.toLocaleString("en-US", { timeZone: "UTC" });
    const tzStr = d.toLocaleString("en-US", { timeZone: tz });
    const utcDate = new Date(utcStr);
    const tzDate = new Date(tzStr);
    const offsetMs = utcDate - tzDate;
    return new Date(d.getTime() + offsetMs);
  }, []);

  // Generate time slots for a selected date.
  //
  // Replaced the old useMemo with a useEffect-driven async fetch so the
  // generator can:
  //   1) subtract existing calendar_events (with buffer_before/after expansion)
  //   2) cap by max_per_day (skip the day entirely if booking count >= cap)
  //   3) enforce min_notice_hours from NOW (was broken — see CalendarPicker)
  //   4) enforce max_advance_days from NOW
  //
  // The candidate-slot loop (working-hours block stride) is unchanged;
  // only the post-filter and the day-level fetches are new. buffer_after
  // continues to serve double duty as the inter-slot stride within a
  // working block AND as the post-event buffer in conflict checks.
  const [timeSlots, setTimeSlots] = useState([]);
  useEffect(() => {
    if (!selectedDate || !selectedType) { setTimeSlots([]); return; }
    const dayOfWeek = selectedDate.getDay();
    const avail = availability.find(a => a.day_of_week === dayOfWeek);
    if (!avail) { setTimeSlots([]); return; }
    if (!selectedType.user_id) { setTimeSlots([]); return; }

    let cancelled = false;
    (async () => {
      const y = selectedDate.getFullYear(), mo = selectedDate.getMonth(), da = selectedDate.getDate();
      const duration = selectedType.duration;
      const stride = (selectedType.buffer_after || 0);

      // Day window in host timezone, expressed as UTC instants for query.
      const dayStart = dateInTz(y, mo, da, 0, 0, hostTz);
      const dayEnd = new Date(dayStart.getTime() + 86400000);

      // 1) Cap by max_per_day for this booking type + host.
      const cap = selectedType.max_per_day || 0;
      if (cap > 0) {
        const { count: bookingsToday } = await supabase.from("bookings")
          .select("id", { count: "exact", head: true })
          .eq("host_user_id", selectedType.user_id)
          .eq("booking_type_id", selectedType.id)
          .neq("status", "cancelled")
          .gte("start_time", dayStart.toISOString())
          .lt("start_time", dayEnd.toISOString());
        if (typeof bookingsToday === "number" && bookingsToday >= cap) {
          if (!cancelled) setTimeSlots([]);
          return;
        }
      }

      // 2) Existing calendar events to subtract (with buffer expansion).
      // Cancelled events are excluded so a host's cancel actually frees the
      // slot — without this filter, the row keeps blocking even after status
      // flips to 'cancelled'.
      const { data: existingEvents } = await supabase.from("calendar_events")
        .select("start_time, end_time")
        .eq("user_id", selectedType.user_id)
        .neq("status", "cancelled")
        .gte("end_time", dayStart.toISOString())
        .lt("start_time", dayEnd.toISOString());

      // 3) Build candidate slots from working hours (preserved logic).
      const candidates = [];
      const [startH, startM] = avail.start_time.split(":").map(Number);
      const [endH, endM] = avail.end_time.split(":").map(Number);
      let h = startH, m = startM;
      while (h * 60 + m + duration <= endH * 60 + endM) {
        const slotStart = dateInTz(y, mo, da, h, m, hostTz);
        const slotEnd = new Date(slotStart.getTime() + duration * 60000);
        candidates.push({ start: slotStart, end: slotEnd });
        m += duration + stride;
        while (m >= 60) { h++; m -= 60; }
      }

      // 4) Post-filter: min_notice, max_advance, conflicts (with buffers).
      const now = Date.now();
      const minNoticeMs = (selectedType.min_notice_hours || 0) * 3600000;
      const maxAdvanceMs = (selectedType.max_advance_days || 0) * 86400000;
      const bufferBeforeMs = (selectedType.buffer_before || 0) * 60000;
      const bufferAfterMs = (selectedType.buffer_after || 0) * 60000;

      const filtered = candidates.filter((slot) => {
        const slotStartMs = slot.start.getTime();
        const slotEndMs = slot.end.getTime();
        if (minNoticeMs > 0 && slotStartMs < now + minNoticeMs) return false;
        if (maxAdvanceMs > 0 && slotStartMs > now + maxAdvanceMs) return false;
        if (slotStartMs <= now) return false;
        const expandedStart = slotStartMs - bufferBeforeMs;
        const expandedEnd = slotEndMs + bufferAfterMs;
        for (const ev of (existingEvents || [])) {
          const evStart = new Date(ev.start_time).getTime();
          const evEnd = new Date(ev.end_time).getTime();
          if (expandedStart < evEnd && expandedEnd > evStart) return false;
        }
        return true;
      });

      if (!cancelled) setTimeSlots(filtered);
    })();

    return () => { cancelled = true; };
  }, [selectedDate, selectedType, availability, hostTz, dateInTz]);

  // Submit booking
  const handleBook = async () => {
    if (!guestName.trim() || !guestEmail.trim()) return;
    setSubmitting(true);
    try {
      const { data, error: err } = await supabase.rpc("create_public_booking", {
        p_booking_type_id: selectedType.id,
        p_guest_name: guestName.trim(),
        p_guest_email: guestEmail.trim(),
        p_guest_phone: guestPhone.trim() || "",
        p_start_time: selectedSlot.start.toISOString(),
        p_end_time: selectedSlot.end.toISOString(),
        p_answers: JSON.stringify(answers)
      });
      console.log("[Booking] RPC result:", data, "error:", err);
      if (err) {
        console.error("[BookingPage] Failed to book appointment:", err);
        // Postgres errcode 23514 = check_violation. The RPC raises this
        // for min_notice / max_advance / max_per_day / conflict failures
        // — which, from the booker's perspective, all mean "this slot
        // isn't actually available". Map to a user-friendly message,
        // bounce back to time selection, and refresh the slot list.
        if (err.code === "23514" || /violates|exceeds|conflicts|maximum bookings/i.test(err.message || "")) {
          toast?.error?.("This time is no longer available — please pick another.");
          setSelectedSlot(null);
          setStep("time");
        } else {
          toast?.error?.("Failed to book appointment: " + (err.message || "unknown error"));
        }
        setSubmitting(false);
        return;
      }
      if (data?.error) { console.error("[BookingPage] Booking RPC returned error:", data.error); toast?.error?.("Failed to book appointment: " + data.error); setSubmitting(false); return; }
      setConfirmation(data);
      setStep("confirmed");
      // Send confirmation email and push to calendar (fire and forget)
      if (data?.booking_id) {
        fetch("/api/calendar/sign-booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingId: data.booking_id }),
        }).then(r => r.json()).then(({ token }) => {
          if (!token) return;
          fetch("/api/calendar/send-booking-confirmation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookingId: data.booking_id, token }),
          }).catch(() => {});
          fetch("/api/calendar/push-booking", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookingId: data.booking_id, token }),
          }).catch(() => {});
        }).catch(() => {});
      }
    } catch (e) { console.error("[BookingPage] Booking exception:", e); toast?.error?.("Failed to book appointment: " + (e.message || "unknown error")); }
    setSubmitting(false);
  };

  // ── LOADING ──
  if (loading) return (
    <div style={styles.loadWrap}>
      <style>{`@keyframes bookingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes bookingPulse { 0%,100% { opacity: 0.4; } 50% { opacity: 0.8; } }`}</style>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(217,219,221,0.12)", borderTopColor: brandColor, animation: "bookingSpin 0.7s linear infinite", margin: "0 auto 16px" }} />
        <div style={{ fontSize: 13, color: "rgba(250,250,250,0.5)", animation: "bookingPulse 2s ease-in-out infinite" }}>Loading booking page...</div>
      </div>
    </div>
  );

  // ── ERROR ──
  if (error) return (
    <div style={styles.loadWrap}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
        <div style={{ fontSize: 20, fontWeight: 400, color: "#FAFAFA", marginBottom: 6 }}>Page not found</div>
        <div style={{ fontSize: 14, color: "#FAFAFA" }}>This scheduling link doesn't exist or has been removed.</div>
      </div>
    </div>
  );

  // ── MAIN RENDER ──
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px", background: "#2C2E33", fontFamily: "'proxima-nova', -apple-system, sans-serif" }}>
      <style>{`
        html, body, #root { height: auto !important; min-height: 100vh; overflow: auto !important; background: #2C2E33; }
        @keyframes bookingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .bf-input:focus { border-color: ${brandColor} !important; box-shadow: 0 0 0 3px ${brandColor}18 !important; }
        .bf-slot:hover { background: ${brandColor} !important; color: #fff !important; }
        .bf-primary:hover { filter: brightness(1.08); }
      `}</style>
      <div style={{ width: "100%", maxWidth: 820, background: "#33363D", borderRadius: window.innerWidth < 768 ? 0 : 12, border: window.innerWidth < 768 ? "none" : "1px solid rgba(217,219,221,0.08)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", display: "flex", flexDirection: window.innerWidth < 768 ? "column" : "row", minHeight: window.innerWidth < 768 ? "auto" : 500 }}>

        {/* LEFT: Host Info */}
        <div style={{ width: window.innerWidth < 768 ? "100%" : 280, flexShrink: 0, padding: window.innerWidth < 768 ? "24px 20px" : "32px 28px", borderRight: window.innerWidth < 768 ? "none" : "1px solid rgba(217,219,221,0.08)", borderBottom: window.innerWidth < 768 ? "1px solid rgba(217,219,221,0.08)" : "none", display: "flex", flexDirection: "column" }}>
          {page?.logo_url && <img src={page.logo_url} alt="" style={{ maxHeight: 36, maxWidth: 200, objectFit: "contain", marginBottom: 20 }} />}
          {selectedType?.scheduling_type === "round_robin" ? (
            <>
              {teamMembers.filter(m => m.avatar_url).length > 0 && (
                <div style={{ display: "flex", marginBottom: 14 }}>
                  {teamMembers.filter(m => m.avatar_url).slice(0, 5).map((m, i) => (
                    <img key={m.id} src={m.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: "cover", border: "2px solid #33363D", marginLeft: i > 0 ? -10 : 0, position: "relative", zIndex: 5 - i }} />
                  ))}
                  {teamMembers.filter(m => m.avatar_url).length > 5 && (
                    <div style={{ width: 40, height: 40, borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.08)", border: "2px solid #33363D", fontSize: 10, color: "rgba(217,219,221,0.5)", marginLeft: -10, position: "relative", zIndex: 0 }}>
                      +{teamMembers.filter(m => m.avatar_url).length - 5}
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 13, color: "rgba(217,219,221,0.5)", marginBottom: 2 }}>{page?.org_name || "Coreflow Team"}</div>
            </>
          ) : (
            <>
              {(page?.avatar_url || host?.avatar_url) && (
                <img src={page?.avatar_url || host?.avatar_url} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", marginBottom: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }} />
              )}
              <div style={{ fontSize: 13, color: "rgba(217,219,221,0.5)", marginBottom: 2 }}>{page?.display_name || host?.name}</div>
            </>
          )}
          {selectedType && (
            <>
              <div style={{ fontSize: 20, fontWeight: 400, color: "#FAFAFA", marginBottom: 12, lineHeight: 1.3 }}>{selectedType.name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(217,219,221,0.5)", marginBottom: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                {selectedType.duration} min
              </div>
              {selectedType.location_type && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(217,219,221,0.5)", marginBottom: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  {selectedType.location_type === "video" ? "Video call" : selectedType.location_type === "phone" ? "Phone call" : selectedType.location_type}
                </div>
              )}
              {selectedType.description && (
                <div style={{ fontSize: 12, color: "rgba(217,219,221,0.4)", lineHeight: 1.6, marginTop: 8, borderTop: "1px solid rgba(217,219,221,0.06)", paddingTop: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {selectedType.description}
                </div>
              )}
            </>
          )}
          {!selectedType && (
            <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA" }}>Book a meeting</div>
          )}
          {/* Footer logo at bottom */}
          <div style={{ marginTop: "auto", paddingTop: 20 }}>
            <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 14, objectFit: "contain", opacity: 0.25 }} />
          </div>
        </div>

        {/* RIGHT: Booking Flow */}
        <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto", maxHeight: "80vh" }}>

          {/* Calendar Step */}
          {(step === "types" || step === "calendar") && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 16 }}>Select a date</div>
              <CalendarPicker
                currentMonth={currentMonth}
                setCurrentMonth={setCurrentMonth}
                selectedDate={selectedDate}
                onSelect={(d) => { setSelectedDate(d); setStep("time"); }}
                availableDays={availableDays}
                brandColor={brandColor}
                minNotice={selectedType?.min_notice_hours || 2}
                maxAdvance={selectedType?.max_advance_days || 60}
              />
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(217,219,221,0.4)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                <Select value={guestTz} onChange={e => setGuestTz(e.target.value)}
                  style={{ flex: 1, border: "none", background: "none", fontSize: 11, color: "rgba(217,219,221,0.5)", cursor: "pointer", outline: "none", padding: "2px 0" }}>
                  {TIMEZONE_OPTIONS.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
                </Select>
              </div>
            </div>
          )}

          {/* Time Step */}
          {step === "time" && (
            <div>
              <button onClick={() => { setStep("calendar"); setSelectedSlot(null); }} style={styles.backBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                Back
              </button>
              <div style={{ fontSize: 13, color: "rgba(217,219,221,0.6)", marginBottom: 4 }}>
                {selectedDate?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 16 }}>Select a time</div>
              {timeSlots.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: "rgba(217,219,221,0.5)", fontSize: 13 }}>No available times on this date</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {timeSlots.map((slot, i) => (
                    <button key={i} onClick={() => { setSelectedSlot(slot); setStep("form"); }}
                      className="bf-slot"
                      style={{ ...styles.slotBtn, borderColor: brandColor + "20" }}>
                      {slot.start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: guestTz })}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Form Step */}
          {step === "form" && (
            <div>
              <button onClick={() => setStep("time")} style={styles.backBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                Back
              </button>
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 12, marginBottom: 16, border: "1px solid rgba(217,219,221,0.06)" }}>
                <div style={{ fontSize: 12, color: "#FAFAFA" }}>
                  {selectedDate?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                  {" · "}
                  {selectedSlot?.start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: guestTz })}
                  {" – "}
                  {selectedSlot?.end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: guestTz })}
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 14 }}>Your details</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label style={styles.label}>Name *</label>
                  <input autoFocus value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Your full name" className="bf-input" style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Email *</label>
                  <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="you@example.com" className="bf-input" style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Phone</label>
                  <input type="tel" value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="(optional)" className="bf-input" style={styles.input} />
                </div>
                {(selectedType?.questions || []).map((q, i) => (
                  <div key={i}>
                    <label style={styles.label}>{q}</label>
                    <textarea value={answers[q] || ""} onChange={e => setAnswers(prev => ({ ...prev, [q]: e.target.value }))} rows={2} className="bf-input" style={{ ...styles.input, resize: "vertical", fontFamily: "inherit" }} />
                  </div>
                ))}
              </div>
              <button onClick={handleBook} disabled={submitting || !guestName.trim() || !guestEmail.trim()}
                className="bf-primary"
                style={{ ...styles.primaryBtn, background: brandColor, opacity: submitting || !guestName.trim() || !guestEmail.trim() ? 0.5 : 1 }}>
                {submitting ? "Booking..." : "Confirm Booking"}
              </button>
            </div>
          )}

          {/* Confirmed */}
          {step === "confirmed" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(46,139,87,0.1)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E8B57" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 400, color: "#FAFAFA", margin: "0 0 6px" }}>You're booked!</h2>
              <p style={{ fontSize: 14, color: "rgba(217,219,221,0.6)", margin: "0 0 20px", lineHeight: 1.6 }}>
                {confirmation?.confirmation || selectedType?.confirmation_message || "We'll see you soon."}
              </p>
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 14, border: "1px solid rgba(217,219,221,0.06)", textAlign: "left" }}>
                <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{selectedType?.name}</div>
                <div style={{ fontSize: 12, color: "rgba(217,219,221,0.5)", marginTop: 4 }}>
                  {selectedDate?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                  {" · "}
                  {selectedSlot?.start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: guestTz })}
                  {" – "}
                  {selectedSlot?.end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: guestTz })}
                </div>
              </div>
              <p style={{ fontSize: 12, color: "rgba(217,219,221,0.4)", marginTop: 16 }}>A confirmation has been sent to {guestEmail}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CALENDAR PICKER ──
function CalendarPicker({ currentMonth, setCurrentMonth, selectedDate, onSelect, availableDays, brandColor, minNotice, maxAdvance }) {
  const year = currentMonth.getFullYear();
  const mo = currentMonth.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  // minDate is anchored to NOW (not midnight) so a 2-hour min_notice
  // means "2 hours from this exact moment". The previous version used
  // today.setHours(0,0,0,0) before adding the offset, which let
  // same-day bookers schedule inside the notice window.
  const now = new Date();
  const minDate = new Date(now.getTime() + minNotice * 3600000);
  // maxDate is day-level so the midnight anchor is correct here — we
  // want N full days from today, not N days minus current-time-of-day.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today.getTime() + maxAdvance * 86400000);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const canPrev = new Date(year, mo, 1) > today;
  const canNext = new Date(year, mo + 1, 1) < maxDate;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button onClick={() => canPrev && setCurrentMonth(new Date(year, mo - 1, 1))}
          style={{ ...styles.navBtn, opacity: canPrev ? 1 : 0.25, cursor: canPrev ? "pointer" : "default" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{MONTHS[mo]} {year}</span>
        <button onClick={() => canNext && setCurrentMonth(new Date(year, mo + 1, 1))}
          style={{ ...styles.navBtn, opacity: canNext ? 1 : 0.25, cursor: canNext ? "pointer" : "default" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {DAYS.map(d => <div key={d} style={styles.dayHeader}>{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={"e" + i} />;
          const date = new Date(year, mo, day);
          const dayOfWeek = date.getDay();
          // Day is bookable if its END (next-day midnight) is past
          // minDate. Same-day booking is allowed when at least some of
          // today still falls outside the min_notice window — the
          // slot-level filter then enforces the exact "now + N hours"
          // rule per slot. Without this, a 4pm visitor with a 2h
          // min_notice would see today disabled even though 6pm-onward
          // was bookable.
          const dayEnd = new Date(year, mo, day + 1);
          const isAvailable = availableDays.includes(dayOfWeek) && dayEnd > minDate && date <= maxDate;
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
          const isToday = date.toDateString() === today.toDateString();

          return (
            <button key={i} disabled={!isAvailable} onClick={() => isAvailable && onSelect(date)}
              style={{
                width: "100%", aspectRatio: "1", border: "none", borderRadius: 8,
                cursor: isAvailable ? "pointer" : "default",
                background: isSelected ? brandColor : isToday ? brandColor + "10" : "transparent",
                color: isSelected ? "#fff" : isAvailable ? "#FAFAFA" : "rgba(217,219,221,0.2)",
                fontSize: 14, fontWeight: isSelected || isToday ? 700 : 400,
                transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              onMouseEnter={e => { if (isAvailable && !isSelected) e.currentTarget.style.background = brandColor + "12"; }}
              onMouseLeave={e => { if (isAvailable && !isSelected) e.currentTarget.style.background = isToday ? brandColor + "10" : "transparent"; }}>
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── STYLES ──
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    padding: "40px 16px 60px",
    fontFamily: "'proxima-nova', -apple-system, sans-serif",
  },
  container: {
    width: "100%",
    maxWidth: 420,
  },
  loadWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#2C2E33",
    fontFamily: "'proxima-nova', -apple-system, sans-serif",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  logo: {
    maxHeight: 52, maxWidth: 180, objectFit: "contain",
    borderRadius: 8,
  },
  avatar: {
    width: 48, height: 48, borderRadius: "50%", objectFit: "cover",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  hostName: {
    fontSize: 22, fontWeight: 400, color: "#FAFAFA",
    margin: "14px 0 0", letterSpacing: "-0.02em",
  },
  headline: {
    fontSize: 14, color: "rgba(217,219,221,0.7)", margin: "4px 0 0",
    fontWeight: 400,
  },
  bio: {
    fontSize: 13, color: "rgba(217,219,221,0.5)", margin: "6px 0 0",
    lineHeight: 1.5, maxWidth: 340, marginLeft: "auto", marginRight: "auto",
  },
  card: {
    background: "#33363D",
    borderRadius: 8,
    padding: 24,
    boxShadow: "0 4px 24px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
    border: "1px solid rgba(217,219,221,0.08)",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 16, fontWeight: 400, color: "#FAFAFA",
    margin: "0 0 14px", letterSpacing: "-0.01em",
  },
  typeBtn: {
    display: "flex", alignItems: "center", gap: 12,
    width: "100%", padding: "14px 16px",
    background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8,
    cursor: "pointer", transition: "all 0.2s",
    textDecoration: "none",
  },
  typeDot: {
    width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
  },
  typeName: {
    fontSize: 14, fontWeight: 400, color: "#FAFAFA",
  },
  typeDesc: {
    fontSize: 12, color: "rgba(217,219,221,0.5)", marginTop: 1, lineHeight: 1.4,
  },
  typeDuration: {
    fontSize: 12, fontWeight: 400, color: "#FAFAFA",
    background: "rgba(64,89,173,0.15)", padding: "3px 10px", borderRadius: 6,
    whiteSpace: "nowrap",
  },
  backBtn: {
    display: "flex", alignItems: "center", gap: 4,
    background: "none", border: "none", cursor: "pointer",
    fontSize: 12, color: "rgba(217,219,221,0.6)", fontWeight: 400, padding: 0,
    marginBottom: 12,
  },
  slotBtn: {
    padding: "10px 8px", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8,
    background: "#2C2E33", cursor: "pointer", fontSize: 13, fontWeight: 400,
    color: "#FAFAFA", transition: "all 0.15s", textAlign: "center",
  },
  summaryBox: {
    background: "rgba(255,255,255,0.04)",
    borderRadius: 8, padding: 14,
    marginBottom: 16,
    border: "1px solid rgba(217,219,221,0.08)",
  },
  label: {
    display: "block", fontSize: 12, fontWeight: 400, color: "rgba(217,219,221,0.6)",
    marginBottom: 4,
  },
  input: {
    width: "100%", padding: "10px 14px",
    border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8,
    background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 14,
    outline: "none", transition: "border-color 0.15s",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  primaryBtn: {
    width: "100%", padding: "13px 20px",
    border: "none", borderRadius: 8,
    color: "#D9DBDD", fontSize: 14, fontWeight: 400,
    cursor: "pointer", marginTop: 16,
    transition: "all 0.15s",
    letterSpacing: "-0.01em",
  },
  dayHeader: {
    fontSize: 11, fontWeight: 400, color: "rgba(217,219,221,0.4)",
    textAlign: "center", padding: "6px 0",
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  navBtn: {
    background: "none", border: "none", cursor: "pointer",
    padding: 4, borderRadius: 8, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  footer: {
    textAlign: "center", fontSize: 11, color: "rgba(217,219,221,0.4)",
    marginTop: 20, fontWeight: 400,
  },
};
